// components/members/MemberCarouselCard.js
import React from 'react';
import { Container, Spinner, Button, Image } from 'react-bootstrap';
import { Files } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import useMemberData from '../../hooks/useMemberData';
import useMemberNavigation from '../../hooks/useMemberNavigation';
import { MEMBER_TYPES, getOrdinalSuffix } from '../../utils/membersConfig';
import '../../App.css';

// Optional: Import Capacitor modules only if available
let Haptics, ImpactStyle;
try {
  ({ Haptics, ImpactStyle } = require('@capacitor/haptics'));
} catch (error) {
  // console.log('Capacitor haptics not available');
}

const handleHapticsClick = async (callback) => {
  if (Haptics && ImpactStyle) {
    try {
      await Haptics.impact({ style: ImpactStyle.Light });
    } catch (error) {
      // Haptics not supported
    }
  }
  callback();
  window.scrollTo(0, 0);
};

/**
 * MemberCarouselCard - Displays a member in the carousel card format
 * Matches the styling of the old HouseOfRepsDisplay component
 */
const MemberCarouselCard = ({ memberType = MEMBER_TYPES.HOUSE, skip = 0 }) => {
  const { navigateToMemberFromRaw } = useMemberNavigation();
  const { data, loading, error } = useMemberData(memberType, skip + 1);

  // Get the member at the skip index
  const member = data?.[skip];

  const handleClick = () => {
    navigateToMemberFromRaw(member);
  };

  if (loading) {
    return (
      <div id='feedDisplayCaro'>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '150px' }}>
          <Spinner id='spinner' animation="grow" variant="light" />
        </div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div id='feedDisplayCaro'>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '150px' }}>
          <p style={{ color: 'var(--text-primary)', fontSize: '14px' }}>
            {error ? 'Error loading member' : 'No member available'}
          </p>
        </div>
      </div>
    );
  }

  const fullName = `${member.first_name} ${member.last_name}`;
  const imageSource = member.depication?.imageUrl;
  const latestTerm = member.terms?.[member.terms.length - 1] || {};
  const { chamber, stateName, district, startYear } = latestTerm;

  // Determine the "View More" link based on member type
  const viewMoreLink = memberType === MEMBER_TYPES.SENATE 
    ? '/us/senators' 
    : '/us/houseofreps';

  return (
    <div id='feedDisplayCaro'>
      <Container id='repContainer' onClick={handleClick}>
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {imageSource && (
            <div id='profileImage'>
              <Image id='profileImage-image' src={imageSource} />
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '20px' }}>
            <div style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: 'bold' }}>
              {fullName}
            </div>
            <div style={{ color: 'var(--text-primary)', fontSize: '16px' }}>
              U.S. {chamber}
            </div>
            {district && (
              <div style={{ color: 'var(--text-primary)', fontSize: '16px' }}>
                {getOrdinalSuffix(district)} District {stateName}
              </div>
            )}
            {!district && stateName && (
              <div style={{ color: 'var(--text-primary)', fontSize: '16px' }}>
                {stateName}
              </div>
            )}
            <div style={{ color: 'var(--text-primary)', fontSize: '16px' }}>
              {startYear}
            </div>
          </div>
        </div>
      </Container>
      
      <Link 
        onClick={() => handleHapticsClick(() => {})} 
        id='pageLink' 
        to={viewMoreLink}
      >
        <Button id='repsViewMore'>
          <Files size={20} /> View More
        </Button>
      </Link>
    </div>
  );
};

export default MemberCarouselCard;